import { Card, Form, FormButtonRow, FormSection, Heading, Icon, IconButton, Link } from '@ksyos/design-system';
import { pick } from 'lodash';
import { useCallback, useEffect, useReducer, useState } from 'react';
import { useSelector } from 'react-redux';
import type { OrganizationDataError } from '@common/interfaces/api/organizationDataErrorsApi';
import type { IReduxState } from '@frontend/configureStore';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ManagerAuthorization, PractitionerRole } from '@common/enums';
import { emptyUuid } from '@common/utils/uuid';
import {
    type IOrganizationGeneralSettings,
    isAgbCodeRequiredForOrganizationProps as isAgbCodeRequiredForOrganizationProps,
    organizationGeneralSettingsSchema,
    organizationSchema,
} from '@common/validation/organization';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization, hasOneAuthorization, scrollValidationErrorIntoView } from '@frontend/utils';
import { formatStringIntoDutchPostalCode } from '@frontend/utils/formatStringIntoDutchPostalCode';
import { hasPractitionerRole } from '@frontend/utils/hasPractitionerRole';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';
import type { VektisOrganizationSettings } from '../utils/vektisUtils';
import { FormContextProvider } from '../../../elements/formContext';
import { CancelButton } from '../../../userDataManagement/CancelButton';
import { SaveButton } from '../../../userDataManagement/SaveButton';
import { OrganizationStatusAlert } from '../OrganizationStatusAlert';
import {
    errorTypesByOrganizationProperty,
    updateValidationErrorsForDataErrors,
} from '../utils/organizationDataErrorsValidationUtils';
import {
    OrganizationRelationshipsActions,
    organizationRelationshipsReducer,
} from '../utils/organizationRelationshipsReducer';
import { OrganizationChangeNameModal } from './../OrganizationChangeNameModal';
import { CountryStateFormGroup } from './CountryStateFormGroup';
import EditableTextField from './EditableTextField';
import { HouseNumberFormGroup } from './HouseNumberFormGroup';
import { OrganizationAgbCodes } from './OrganizationAgbCodes';
import { OrganizationDescriptionFormGroup } from './OrganizationDescriptionFormGroup';
import { OrganizationKgoStatusCommentFormGroup } from './OrganizationKgoStatusCommentFormGroup';
import { OrganizationKgoStatusFormGroup } from './OrganizationKgoStatusFormGroup';
import { OrganizationLogs } from './OrganizationLogs';
import { OrganizationRelationshipsFormGroup } from './OrganizationRelationshipsFormGroup';
import { OrganizationStructureTypeFormGroup } from './OrganizationStructureTypeFormGroup';
import { OrganizationTypeFormGroup } from './OrganizationTypeFormGroup';
import { VektisCompareModal } from './VektisCompareModal';

type IOrganizationGeneralSettingsProps = {
    editMode: boolean;
    focusField?: string;
    organizationId: string;
};

export function OrganizationGeneralSettings({
    editMode,
    focusField,
    organizationId,
}: Readonly<IOrganizationGeneralSettingsProps>) {
    const [dataErrors, setDataErrors] = useState<OrganizationDataError[]>([]);
    const [isEditing, setIsEditing] = useState(editMode);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [relationshipState, dispatchRelationshipStateAction] = useReducer(
        organizationRelationshipsReducer<IOrganizationGeneralSettings>,
        {}
    );
    const [originalOrganization, setOriginalOrganization] = useState<IOrganizationGeneralSettings>();
    const [originalDataErrors, setOriginalDataErrors] = useState<OrganizationDataError[]>([]);

    const [isOrganizationNameModalOpen, setIsOrganizationNameModalOpen] = useState(false);
    const [isVektisCompareModalOpen, setIsVektisCompareModalOpen] = useState(false);

    const isOrganizationPayoutManager = hasPractitionerRole(
        useSelector((reduxState: IReduxState) => reduxState.authentication).employmentPractitionerRoles,
        PractitionerRole.organizationPayoutManager
    );
    const abortableFetch = useAbortableFetch();
    if (!isOrganizationPayoutManager) {
        assertHasAuthorization(ManagerAuthorization.organizationRead);
    }

    const canEdit = hasOneAuthorization([
        ManagerAuthorization.organizationAddEdit,
        ManagerAuthorization.contractFundingDataAddEdit,
    ]);

    const hasOrganizationAddEditAuthorization = hasOneAuthorization(ManagerAuthorization.organizationAddEdit);

    const translate = getSelectedLanguage();

    useEffect(() => {
        loadOrganization(organizationId);
        loadOrganizationDataErrors(organizationId);
    }, [organizationId]);

    const isAgbCodeRequired = isAgbCodeRequiredForOrganizationProps({
        isActive: relationshipState.organization?.isActive,
        kgoStatus: relationshipState.organization?.kgoStatus,
        organizationStructureType: relationshipState.organization?.organizationStructureType,
        organizationType: relationshipState.organization?.organizationType,
    });

    const generalSettingsForm = useCallback(
        (generalSettingsFormNode: HTMLFormElement) => {
            if (generalSettingsFormNode !== null && editMode && focusField) {
                const input: HTMLInputElement | null = generalSettingsFormNode.querySelector(
                    `input[name=${focusField}]`
                );
                if (input) {
                    input.scrollIntoView({ block: 'center' });
                    input.focus();
                }
            }
        },
        [editMode, focusField]
    );

    if (!relationshipState.organization) {
        return null;
    }

    return (
        <>
            <FormContextProvider
                value={{
                    errors: errors,
                    item: relationshipState.organization,
                    onBlur: handleBlur,
                    onChange: handleChange,
                    onDoValidate: validateOne,
                    placeholderGroupName: 'organizationGeneralSettings',
                }}
            >
                {hasOrganizationAddEditAuthorization && originalOrganization?.kgoStatus && (
                    <OrganizationStatusAlert
                        organization={originalOrganization}
                        organizationDataErrors={originalDataErrors}
                    />
                )}
                <Card>
                    <Card.Transition type={isEditing ? 'edit' : 'read'} />

                    {originalOrganization && (
                        <OrganizationChangeNameModal
                            isOpen={isOrganizationNameModalOpen}
                            onClose={() => setIsOrganizationNameModalOpen(false)}
                            onSubmitChangeName={handleSubmitChangeName}
                        />
                    )}

                    <Card.Header
                        action={
                            canEdit && !isEditing ? (
                                <IconButton
                                    icon="edit-light"
                                    labelText="edit"
                                    onClick={() => {
                                        loadOrganization(organizationId);
                                        setIsEditing(true);
                                    }}
                                />
                            ) : canEditProperty('agbCodes') ? (
                                <>
                                    <Icon
                                        color="primary"
                                        icon="todo-list"
                                        size={16}
                                        sx={{ mr: 2 }}
                                    />
                                    <Link
                                        onClick={openVektisModal}
                                        variant="button"
                                    >
                                        {translate.organization.autoFill.searchVektis}
                                    </Link>
                                </>
                            ) : undefined
                        }
                        title={translate.organization.generalInformation}
                    />

                    <Card.Body>
                        <Form
                            formRef={generalSettingsForm}
                            submitOnEnter={false}
                        >
                            <FormSection>
                                <EditableTextField
                                    canEdit={canEditProperty('name')}
                                    errors={errors}
                                    fieldKey="name"
                                    isRequired={true}
                                    label="organization.name"
                                />
                                <EditableTextField
                                    canEdit={canEditProperty('nameStatutory')}
                                    errors={errors}
                                    fieldKey="nameStatutory"
                                    label="organization.nameStatutory"
                                />
                                <EditableTextField
                                    canEdit={canEditProperty('alias')}
                                    errors={errors}
                                    fieldKey="alias"
                                    label="organization.alias"
                                />
                                <EditableTextField
                                    canEdit={canEditProperty('kvkNumber')}
                                    errors={errors}
                                    fieldKey="kvkNumber"
                                    label="organization.kvkNumber"
                                    maxLength={8}
                                />
                                <OrganizationStructureTypeFormGroup
                                    canEdit={canEditProperty('organizationStructureType')}
                                    onChange={handleChange}
                                    organizationStructureType={relationshipState.organization.organizationStructureType}
                                />
                                <OrganizationTypeFormGroup
                                    canEdit={canEditProperty('organizationType')}
                                    onChange={handleChange}
                                    organizationType={relationshipState.organization.organizationType}
                                />
                                {!isOrganizationPayoutManager && (
                                    <OrganizationDescriptionFormGroup
                                        canEdit={canEditProperty('description')}
                                        errors={errors}
                                        onChange={handleChange}
                                        organization={relationshipState.organization}
                                    />
                                )}

                                <OrganizationAgbCodes
                                    agbCodes={relationshipState.organization.agbCodes}
                                    dataErrors={dataErrors}
                                    errors={errors}
                                    isEditing={isEditing && canEditProperty('agbCodes')}
                                    isRequired={isAgbCodeRequired}
                                    onChange={(agbCodes) => handleChange('agbCodes', agbCodes)}
                                    onRemoveValidationErrors={handleRemoveValidationErrors}
                                />
                                <OrganizationRelationshipsFormGroup
                                    canEdit={canEditProperty('organizationRelationships')}
                                    dispatchRelationshipStateAction={dispatchRelationshipStateAction}
                                    errors={errors}
                                    onRemoveValidationErrors={handleRemoveValidationErrors}
                                    organization={relationshipState.organization}
                                    organizationId={organizationId}
                                />
                            </FormSection>
                            <FormSection title={translate.organization.contactDetails}>
                                <EditableTextField
                                    canEdit={canEditProperty('phoneNumber')}
                                    errors={errors}
                                    fieldKey="phoneNumber"
                                    isRequired={true}
                                    label="common.phoneNumber"
                                />
                                <EditableTextField
                                    canEdit={canEditProperty('emergencyNumber')}
                                    errors={errors}
                                    fieldKey="emergencyNumber"
                                    isRequired={false}
                                    label="organization.emergencyNumber"
                                    tooltipText={translate.organization.emergencyNumberTooltip}
                                />
                                <EditableTextField
                                    canEdit={canEditProperty('emailAddressRequestInbox')}
                                    errors={errors}
                                    fieldKey="emailAddressRequestInbox"
                                    isRequired={true}
                                    label="common.emailAddress"
                                />
                                <EditableTextField
                                    canEdit={canEditProperty('url')}
                                    errors={errors}
                                    fieldKey="url"
                                    label="common.website"
                                />

                                <Heading
                                    mb={4}
                                    size="medium"
                                >
                                    {translate.organization.address}
                                </Heading>
                                <EditableTextField
                                    canEdit={canEditProperty('street')}
                                    errors={errors}
                                    fieldKey="street"
                                    label="common.street"
                                />

                                <HouseNumberFormGroup
                                    canEdit={canEditProperty('houseNumber')}
                                    errors={errors}
                                    organization={relationshipState.organization}
                                />

                                <EditableTextField
                                    canEdit={canEditProperty('zip')}
                                    errors={errors}
                                    fieldKey="zip"
                                    isRequired={true}
                                    label="common.zipcode"
                                />
                                <EditableTextField
                                    canEdit={canEditProperty('city')}
                                    errors={errors}
                                    fieldKey="city"
                                    isRequired={true}
                                    label="common.city"
                                />

                                <CountryStateFormGroup
                                    canEdit={canEditProperty('state')}
                                    errors={errors}
                                    onChange={handleChange}
                                    organization={relationshipState.organization}
                                    validateOne={validateOne}
                                />
                            </FormSection>
                            {!isOrganizationPayoutManager && (
                                <FormSection title={translate.organization.verification}>
                                    <OrganizationKgoStatusFormGroup
                                        canEdit={canEditProperty('kgoStatus')}
                                        errors={errors}
                                        kgoStatus={relationshipState.organization.kgoStatus}
                                        onChange={handleChange}
                                    />
                                    <OrganizationKgoStatusCommentFormGroup
                                        canEdit={canEditProperty('kgoStatusComment')}
                                        errors={errors}
                                        kgoStatus={relationshipState.organization.kgoStatus}
                                        kgoStatusComment={relationshipState.organization.kgoStatusComment}
                                        onChange={handleChange}
                                    />
                                </FormSection>
                            )}
                            {isEditing && (
                                <FormButtonRow>
                                    <CancelButton
                                        disabled={isSaving}
                                        onCancel={async () => handleCancel()}
                                    />
                                    <SaveButton
                                        disabled={isSaving}
                                        onSave={() => handleSubmit(relationshipState.organization)}
                                    />
                                </FormButtonRow>
                            )}
                        </Form>
                    </Card.Body>
                    {!isEditing && !isOrganizationPayoutManager && (
                        <Card.Footer>
                            <OrganizationLogs organizationId={organizationId} />
                        </Card.Footer>
                    )}
                </Card>
            </FormContextProvider>
            <VektisCompareModal
                isOpen={isVektisCompareModalOpen}
                onAutoFill={(autoFillOrg) => {
                    setIsVektisCompareModalOpen(false);
                    handleAutoFill(autoFillOrg);
                }}
                onClose={() => setIsVektisCompareModalOpen(false)}
                organization={relationshipState.organization}
            />
        </>
    );

    function canEditProperty(property: keyof IOrganizationGeneralSettings): boolean {
        if (!isEditing) {
            return false;
        }

        if (hasOneAuthorization(ManagerAuthorization.organizationAddEdit)) {
            return true;
        }

        if (
            (property === 'organizationStructureType' || property === 'organizationType') &&
            hasOneAuthorization(ManagerAuthorization.contractFundingDataAddEdit)
        ) {
            return true;
        }

        return false;
    }

    function openVektisModal(): void {
        setIsVektisCompareModalOpen(true);
    }

    function handleAutoFill(autoFillOrganization: Partial<VektisOrganizationSettings>) {
        if (!relationshipState.organization) {
            return;
        }

        const organizationUpdate = { ...relationshipState.organization, ...autoFillOrganization };
        dispatchRelationshipStateAction({
            payload: organizationUpdate,
            type: OrganizationRelationshipsActions.setOrganization,
        });
    }

    async function loadOrganization(id: string) {
        const url = `/api/organizations/${id}`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setOriginalOrganization(result.json);
        const validatedResult = organizationSchema.schema.validate(result.json, {
            abortEarly: false,
            convert: true,
            presence: 'required',
            stripUnknown: { arrays: true, objects: true },
        });

        dispatchRelationshipStateAction({
            payload: validatedResult.value,
            type: OrganizationRelationshipsActions.setOrganization,
        });
    }

    async function loadOrganizationDataErrors(id: string) {
        if (!hasOrganizationAddEditAuthorization) {
            return;
        }
        const url = `/api/organizations/${id}/dataErrors`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setOriginalDataErrors(result.json);
    }

    function handleBlur<K extends keyof IOrganizationGeneralSettings>(name: K, value: IOrganizationGeneralSettings[K]) {
        const trimmedValue = typeof value === 'string' ? value.trim() : value;

        dispatchRelationshipStateAction({
            payload: { key: name, value: trimmedValue },
            type: OrganizationRelationshipsActions.updateOrganizationKeyValue,
        });
    }

    function handleChange<K extends keyof IOrganizationGeneralSettings>(
        name: K,
        value: IOrganizationGeneralSettings[K]
    ) {
        let newValue = value;

        if (relationshipState.organization?.country === 'NL' || relationshipState.organization?.country === null) {
            newValue =
                name === 'zip' && typeof value === 'string'
                    ? (formatStringIntoDutchPostalCode(value) as IOrganizationGeneralSettings[K])
                    : value;
        }

        if (relationshipState.organization) {
            dispatchRelationshipStateAction({
                payload: { key: name, value: newValue },
                type: OrganizationRelationshipsActions.updateOrganizationKeyValue,
            });
        }
    }

    function handleRemoveValidationErrors(keys: string[]): void {
        setErrors((prevErrors) => keys.reduce((acc, key) => acc.remove(key), prevErrors));
    }

    async function validateOne(name: keyof Omit<IOrganizationGeneralSettings, 'nameStartDate'>) {
        if (relationshipState.organization) {
            const filteredGeneralSettings = {
                ...pick(relationshipState.organization),
            };
            const { dataErrors: validationDataErrors, validationErrors } = await updateValidationErrorsForDataErrors(
                updateValidationErrorsForOne(organizationGeneralSettingsSchema, filteredGeneralSettings, errors, name),
                { ...relationshipState.organization, id: organizationId },
                name
            );
            setErrors(validationErrors);
            setDataErrors(validationDataErrors);
        }
    }

    function handleCancel() {
        if (!originalOrganization) {
            return;
        }
        dispatchRelationshipStateAction({
            payload: originalOrganization,
            type: OrganizationRelationshipsActions.setOrganization,
        });
        setErrors(emptyValidationErrors);
        setDataErrors([]);
        setIsEditing(false);
    }

    async function handleSubmitChangeName(nameStartDate: string) {
        if (relationshipState.organization && originalOrganization) {
            const updatedOrganizationSettings = {
                ...relationshipState.organization,
                nameStartDate,
            };
            await handleSubmit(updatedOrganizationSettings);
        }

        setIsOrganizationNameModalOpen(false);
    }

    async function handleSubmit(updatedOrganizationSettings?: IOrganizationGeneralSettings) {
        if (!updatedOrganizationSettings) {
            return;
        }
        if (!originalOrganization) {
            return;
        }

        const { errors: schemaValidationErrors, item } = validate(
            organizationGeneralSettingsSchema,
            updatedOrganizationSettings
        );

        const { dataErrors: validationDataErrors, validationErrors } = await updateValidationErrorsForDataErrors(
            schemaValidationErrors,
            { ...relationshipState.organization, id: organizationId },
            ...errorTypesByOrganizationProperty.keys()
        );

        if (validationErrors.size > 0 || !item) {
            setErrors(validationErrors);
            setDataErrors(validationDataErrors);
            delayedScrollToValidationError();

            return;
        }

        if (
            updatedOrganizationSettings.name !== originalOrganization.name &&
            !updatedOrganizationSettings.nameStartDate
        ) {
            return setIsOrganizationNameModalOpen(true);
        }

        updatedOrganizationSettings.organizationRelationships =
            updatedOrganizationSettings.organizationRelationships.filter(
                (relationship) => relationship.relatedOrganizationId !== emptyUuid
            );

        await saveNewOrganizationGeneralSettings(updatedOrganizationSettings);
    }

    async function saveNewOrganizationGeneralSettings(organizationGeneralSettings: IOrganizationGeneralSettings) {
        setIsSaving(true);

        const url = `/api/organizations/${organizationId}/generalSettings`;
        await abortableFetch.fetchJson(url, 'POST', organizationGeneralSettings);

        await loadOrganization(organizationId);
        await loadOrganizationDataErrors(organizationId);

        setErrors(emptyValidationErrors);
        setDataErrors([]);
        setIsEditing(false);
        setIsSaving(false);
    }

    function delayedScrollToValidationError() {
        requestAnimationFrame(() => {
            scrollValidationErrorIntoView();
        });
    }
}
